import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF8E7]" data-testid="contact-page">
      {/* Header */}
      <div className="bg-white border-b border-[#6B4F2A]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <nav className="flex items-center gap-2 text-sm text-[#574021] mb-4">
            <Link to="/" className="hover:text-[#1F7A63] transition-colors">Home</Link>
            <span className="text-[#6B4F2A]/30">/</span>
            <span className="text-[#2C1E0F] font-medium">Contact Us</span>
          </nav>
          <h1 className="text-3xl md:text-4xl font-black text-[#2C1E0F] tracking-tight" data-testid="contact-title">
            Contact Us
          </h1>
          <p className="text-[#574021] mt-2">
            We'd love to hear from you. Get in touch with us!
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-2xl border border-[#6B4F2A]/10 p-6" data-testid="contact-info-card">
              <h2 className="text-xl font-bold text-[#2C1E0F] mb-6">Get in Touch</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1F7A63]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#1F7A63]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2C1E0F] mb-1">Address</h3>
                    <p className="text-[#574021]" data-testid="contact-address">
                      17/5 A-1 Sadar Cantt<br />
                      Lahore, Pakistan
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1F7A63]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#1F7A63]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2C1E0F] mb-1">Phone</h3>
                    <a
                      href="tel:+923230480609"
                      className="text-[#574021] hover:text-[#1F7A63] transition-colors"
                      data-testid="contact-phone"
                    >
                      +92 323 048 0609
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1F7A63]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#1F7A63]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2C1E0F] mb-1">Email</h3>
                    <a
                      href="mailto:support@naturewayz.com"
                      className="text-[#574021] hover:text-[#1F7A63] transition-colors"
                      data-testid="contact-email"
                    >
                      support@naturewayz.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1F7A63]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-[#1F7A63]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2C1E0F] mb-1">Business Hours</h3>
                    <p className="text-[#574021]">
                      Mon - Sat: 9:00 AM - 6:00 PM<br />
                      Sunday: Closed
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="bg-white rounded-2xl border border-[#6B4F2A]/10 overflow-hidden h-64" data-testid="contact-map">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3401.0144878947584!2d74.36055!3d31.5557!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x391904c0a2abcd!2sLahore%20Cantt%2C%20Lahore%2C%20Pakistan!5e0!3m2!1sen!2s!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="NatureWayz Location"
              />
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-[#6B4F2A]/10 p-6 md:p-8" data-testid="contact-form-card">
              <h2 className="text-xl font-bold text-[#2C1E0F] mb-6">Send Us a Message</h2>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-[#574021] mb-2">Your Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="input-field"
                      required
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#574021] mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="input-field"
                      required
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-[#574021] mb-2">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="input-field"
                      placeholder="+92 XXX XXXXXXX"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#574021] mb-2">Subject</label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="input-field"
                      data-testid="contact-subject-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#574021] mb-2">Your Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={6}
                    className="input-field resize-none"
                    required
                    data-testid="contact-message-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary flex items-center gap-2"
                  data-testid="contact-submit-btn"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
